import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import CertificateCreationBlueprintType from '../../types/CertificateCreationBlueprintType';
import LoadingSpinner from '../Spinner';

import AgentDetailsStep from './components/AgentDetailsStep/AgentDetailsStep';
import ChoosePolicyTypesStep from './components/ChoosePolicyTypesStep';
import OcrProcessingStep from './components/OcrProcessingStep';
import ProcessingStep from './components/ProcessingStep';
import RequirementStep from './components/RequirementsStep';
import ReviewStep from './components/ReviewStep';
import SubmissionProgress from './components/SubmissionProgress';
import SubmissionStep from './components/SubmissionStep';
import UploadStep from './components/UploadStep';

const CertificateCreationFlow = ({
  blueprint,
  job,
  company,
  documentRequirements,
  insuredRequirements,
  itemRequirements,
  languageRequirements,
  policyTypes,
  insured,
  options,
  user,
  insuranceRequirement,
  policyReviewContentHeight = '64vh'
}) => {
  const dispatch = useDispatch();
  const currentStep = useSelector(blueprint.selectCurrentStep);
  const isInitializing = useSelector(blueprint.selectIsInitializing);
  const isLoading = useSelector(blueprint.selectIsLoading);
  const isSubmitting = useSelector(blueprint.selectIsSubmitting);
  const loadingHeaderText = useSelector(blueprint.selectLoadingHeaderText);
  const loadingSubText = useSelector(blueprint.selectLoadingSubText);

  useEffect(() => {
    dispatch(blueprint.setJob(job));
    dispatch(blueprint.setDepartment(job.department));
    dispatch(blueprint.setCompany(company));
    dispatch(blueprint.setInsurancePolicyTypes(policyTypes));
    dispatch(blueprint.setDocumentRequirements(documentRequirements));
    dispatch(blueprint.setInsuredRequirements(insuredRequirements));
    dispatch(blueprint.setInsured(insured));
    dispatch(blueprint.setInsuranceRequirement(insuranceRequirement));
    dispatch(blueprint.setItemRequirements(itemRequirements));
    dispatch(blueprint.setLanguageRequirements(languageRequirements));
    dispatch(blueprint.setRequiredPolicyTypes());
    dispatch(blueprint.setUser(user));
    dispatch(blueprint.setFirstStep(1));
    dispatch(blueprint.setPolicyReviewContentHeight(policyReviewContentHeight));

    if (options) {
      dispatch(blueprint.setOptions(options));

      if (options.showCertHolderInformation) {
        dispatch(blueprint.getUserPolicies());
        dispatch(blueprint.getUserDocuments());
      }
    }

    return () => dispatch(blueprint.reset());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // a synchronous submission blocks until the API has finished the whole job, so the awaited
  // response is the completion signal and a creeping bar can stand in for real progress
  if (isSubmitting && options?.shouldSync) {
    return <SubmissionProgress blueprint={blueprint} />;
  }

  if (isInitializing || isLoading) {
    return (
      <>
        <Box flexFlow="row nowrap" justifyContent="center" mt={8} textAlign="center" w="100%">
          <Text color="greyScale.700" mb={4} textStyle="displayXs600">{loadingHeaderText}</Text>
          <Text color="greyScale.700" textStyle="bodyLg500">{loadingSubText}</Text>
        </Box>
        <LoadingSpinner />
      </>
    );
  }

  switch (currentStep) {
    case 1:
      return (
        <RequirementStep blueprint={blueprint} />
      );
    case 2:
      return (
        <ChoosePolicyTypesStep blueprint={blueprint} />
      );
    case 3:
      return (
        <AgentDetailsStep blueprint={blueprint} />
      );
    case 4:
      return (
        <UploadStep blueprint={blueprint} />
      );
    case 5:
      return (
        <OcrProcessingStep blueprint={blueprint} />
      );
    case 6:
      return (
        <ReviewStep blueprint={blueprint} />
      );
    case 7:
      return (
        <SubmissionStep blueprint={blueprint} />
      );
    default:
      return (
        <LoadingSpinner />
      );
  }
};

CertificateCreationFlow.propTypes = {
  blueprint: CertificateCreationBlueprintType,
  job: PropTypes.shape({ department: PropTypes.shape({}) }),
  company: PropTypes.shape({}),
  documentRequirements: PropTypes.arrayOf(PropTypes.shape({})),
  insured: PropTypes.shape({}),
  itemRequirements: PropTypes.arrayOf(PropTypes.shape({})),
  insuredRequirements: PropTypes.arrayOf(PropTypes.shape({})),
  languageRequirements: PropTypes.arrayOf(PropTypes.shape({})),
  policyTypes: PropTypes.arrayOf(PropTypes.shape({})),
  options: PropTypes.shape({
    ocr: PropTypes.bool,
    aiScan: PropTypes.bool,
    showCertHolderInformation: PropTypes.bool,
    showInsuredInformation: PropTypes.bool,
    showPreviousSubmissions: PropTypes.bool,
    updateInsuredProfilePage: PropTypes.bool,
    afterSubmitRedirectUrl: PropTypes.string,
    chooseAnyPolicyType: PropTypes.bool,
    shouldSync: PropTypes.bool,
    requireAgentDetails: PropTypes.bool
  }),
  user: PropTypes.shape({ userId: PropTypes.number }),
  policyReviewContentHeight: PropTypes.string,
  insuranceRequirement: PropTypes.shape({})
};

export default CertificateCreationFlow;
